import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Report {
  id: string
  name: string
  type: string
  status: string
  period_start: string
  period_end: string
  generated_at: string
  generated_by: string
  file_url: string | null
  file_size_mb: number | null
  summary: any
}

interface Certification {
  id: string
  name: string
  issuer: string
  status: string
  issued_date: string | null
  expiry_date: string | null
  certificate_url: string | null
  scope: string
  target_date?: string
}

interface Regulation {
  id: string
  name: string
  full_name: string
  jurisdiction: string
  applicable: boolean
  requirements: number
  requirements_met: number
  last_assessed: string | null
}

export default function ComplianceReportingPage() {
  const [reports, setReports] = useState<Report[]>([])
  const [certifications, setCertifications] = useState<Certification[]>([])
  const [regulations, setRegulations] = useState<Regulation[]>([])
  const [, setScheduled] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [rptRes, certRes, regRes, schedRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/compliance-reporting/reports'),
      fetch(API_BASE + '/compliance-reporting/certifications'),
      fetch(API_BASE + '/compliance-reporting/regulations?applicable_only=true'),
      fetch(API_BASE + '/compliance-reporting/scheduled'),
      fetch(API_BASE + '/compliance-reporting/statistics')
    ])
    if (rptRes.ok) setReports((await rptRes.json()).reports || [])
    if (certRes.ok) setCertifications((await certRes.json()).certifications || [])
    if (regRes.ok) setRegulations((await regRes.json()).regulations || [])
    if (schedRes.ok) setScheduled((await schedRes.json()).scheduled_reports || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': case 'active': case 'pass': return 'bg-green-100 text-green-800'
      case 'draft': case 'generating': return 'bg-yellow-100 text-yellow-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'expired': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Compliance Reporting</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Generate Report</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Reports</div>
              <div className="text-2xl font-bold">{statistics.total_reports}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Published</div>
              <div className="text-2xl font-bold text-green-600">{statistics.published_reports}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Certifications</div>
              <div className="text-2xl font-bold">{statistics.active_certifications}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expiring Soon</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.expiring_soon}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Regulations</div>
              <div className="text-2xl font-bold">{statistics.applicable_regulations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Compliance</div>
              <div className="text-2xl font-bold text-green-600">{statistics.average_compliance_score}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          {certifications.filter(c => c.status === 'active').map(cert => (
            <div key={cert.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold">{cert.name}</h3>
                  <div className="text-sm text-gray-500">{cert.issuer}</div>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${getStatusColor(cert.status)}`}>{cert.status}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{cert.scope}</p>
              <div className="text-sm text-gray-500">
                {cert.expiry_date && <div>Expires: {new Date(cert.expiry_date).toLocaleDateString()}</div>}
              </div>
              {cert.certificate_url && (
                <button className="mt-3 text-sm text-blue-600 hover:text-blue-800">View Certificate</button>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Compliance Reports</h2></div>
            <div className="divide-y">
              {reports.map(report => (
                <div key={report.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{report.name}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(report.status)}`}>{report.status}</span>
                  </div>
                  {report.summary && (
                    <div className="flex gap-4 text-sm mb-2">
                      {report.summary.controls_passed !== undefined && (
                        <span className="text-green-600">{report.summary.controls_passed}/{report.summary.controls_tested} controls passed</span>
                      )}
                      {report.summary.overall_status && (
                        <span className={`font-medium ${report.summary.overall_status === 'compliant' || report.summary.overall_status === 'pass' ? 'text-green-600' : 'text-red-600'}`}>
                          {report.summary.overall_status}
                        </span>
                      )}
                      {report.summary.implementation_percentage !== undefined && (
                        <span className="text-blue-600">{report.summary.implementation_percentage}% implemented</span>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Generated by {report.generated_by}</span>
                    {report.file_url && (
                      <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Download PDF</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Regulatory Compliance</h3>
              <div className="space-y-3">
                {regulations.map(reg => (
                  <div key={reg.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-sm">{reg.name}</div>
                        <div className="text-xs text-gray-500">{reg.jurisdiction}</div>
                      </div>
                      <span className="text-sm font-semibold text-green-600">
                        {Math.round((reg.requirements_met / reg.requirements) * 100)}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${(reg.requirements_met / reg.requirements) * 100}%` }}></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {reg.requirements_met}/{reg.requirements} requirements met
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Certification Status</h3>
              <div className="space-y-3">
                {certifications.filter(c => c.status === 'in_progress').map(cert => (
                  <div key={cert.id} className="p-3 bg-blue-50 rounded border border-blue-100">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{cert.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(cert.status)}`}>In Progress</span>
                    </div>
                    <div className="text-xs text-gray-500">{cert.issuer}</div>
                    {cert.target_date && (
                      <div className="text-xs text-blue-600 mt-1">Target: {new Date(cert.target_date).toLocaleDateString()}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
